import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import axios from 'axios';

// Backend API URL - configurable per environment
const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

// Recharge Modal Component
// 1 credit = ₹25, with 18% GST added
// Only standard packages available: 100, 500, 1000 credits
const RechargeModal = ({ isOpen, onClose, onSuccess, initialCredits = null }) => {
  const [credits, setCredits] = useState(initialCredits || 100); // Default to Starter Pack or initialCredits
  const [isProcessing, setIsProcessing] = useState(false);

  // Update credits when initialCredits changes
  useEffect(() => {
    if (initialCredits && isOpen) {
      setCredits(initialCredits);
    }
  }, [initialCredits, isOpen]);

  // Calculate pricing
  // Handle empty or invalid credits gracefully
  const validCredits = credits && credits >= 1 ? credits : 0;
  const creditPrice = 25; // ₹25 per credit
  const baseAmount = validCredits * creditPrice;
  const gst = baseAmount * 0.18; // 18% GST
  const totalAmount = baseAmount + gst;

  // Handle package selection - only standard packages available
  const handlePackageSelect = (packageCredits) => {
    setCredits(packageCredits);
  };

  // Initiate PhonePe payment
  const handlePayment = async () => {
    const validCredits = credits && credits >= 1 ? credits : 0;
    if (validCredits < 1) {
      toast.error('Please enter at least 1 credit');
      return;
    }

    setIsProcessing(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        toast.error('Please log in to recharge credits');
        setIsProcessing(false);
        return;
      }

      // Create payment order via backend
      const response = await axios.post(
        `${BACKEND_URL}/api/payment/create-order`,
        {
          credits: validCredits,
          amount: Math.round(totalAmount * 100) // Amount in paise (PhonePe requirement)
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success && response.data.paymentUrl) {
        // PhonePe Standard Checkout - redirect to payment page
        // The payment page will redirect back to /payment-status after payment
        window.location.href = response.data.paymentUrl;
        // Note: setIsProcessing won't be called here as page will redirect
      } else {
        const errorMsg = response.data.error || 'Failed to create payment order';
        toast.error(errorMsg);
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Payment error:', error);
      const errorMsg = error.response?.data?.error || 'Failed to initiate payment';
      toast.error(errorMsg);
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="recharge-modal-overlay" onClick={onClose}>
      <div className="recharge-modal" onClick={(e) => e.stopPropagation()}>
        <div className="recharge-modal-header">
          <h2>Recharge Credits</h2>
          <button className="recharge-modal-close" onClick={onClose}>×</button>
        </div>

        <div className="recharge-modal-content">
          <div className="recharge-info">
            <p className="recharge-rate">1 Credit = ₹25</p>
            <p className="recharge-note">All prices include 18% GST</p>
          </div>

          <div className="recharge-input-section">
            <p style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '1rem', textAlign: 'center' }}>
              Select a package to purchase credits
            </p>
            
            {/* Standard Packages - Only Option */}
            <div className="quick-select-buttons">
              <button
                type="button"
                className={`quick-select-btn ${credits === 100 ? 'active' : ''}`}
                onClick={() => handlePackageSelect(100)}
                title="Starter Pack - 100 Credits"
              >
                <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>Starter Pack</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.25rem' }}>100 Credits</div>
                <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#667eea', marginTop: '0.25rem' }}>
                  ₹2,950
                </div>
              </button>
              <button
                type="button"
                className={`quick-select-btn ${credits === 500 ? 'active' : ''}`}
                onClick={() => handlePackageSelect(500)}
                title="Business Pack - 500 Credits"
              >
                <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>Business Pack</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.25rem' }}>500 Credits</div>
                <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#667eea', marginTop: '0.25rem' }}>
                  ₹14,750
                </div>
              </button>
              <button
                type="button"
                className={`quick-select-btn ${credits === 1000 ? 'active' : ''}`}
                onClick={() => handlePackageSelect(1000)}
                title="Professional Pack - 1,000 Credits"
              >
                <div style={{ fontWeight: '600', fontSize: '1rem', marginBottom: '0.25rem' }}>Professional Pack</div>
                <div style={{ fontSize: '0.85rem', opacity: 0.9, marginBottom: '0.25rem' }}>1,000 Credits</div>
                <div style={{ fontSize: '0.9rem', fontWeight: '600', color: '#667eea', marginTop: '0.25rem' }}>
                  ₹29,500
                </div>
              </button>
            </div>
          </div>

          {/* Price breakdown */}
          {validCredits > 0 && (
            <div className="price-breakdown">
              <div className="price-row">
                <span>Credits ({validCredits.toLocaleString('en-IN')} × ₹25):</span>
                <span>₹{baseAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="price-row">
                <span>GST (18%):</span>
                <span>₹{gst.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
              <div className="price-row total-row">
                <span><strong>Total Amount:</strong></span>
                <span><strong>₹{totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong></span>
              </div>
            </div>
          )}

          <button
            className="recharge-pay-btn"
            onClick={handlePayment}
            disabled={isProcessing || !validCredits || validCredits < 1}
          >
            {isProcessing ? 'Processing...' : validCredits > 0 ? `Pay ₹${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} via PhonePe` : 'Enter credits to proceed'}
          </button>

          <p className="recharge-security-note">
            🔒 Secure payment powered by PhonePe
          </p>
        </div>
      </div>
    </div>
  );
};

export default RechargeModal;

